import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LoadingState } from "@/components/shared/States";

const PHASES = ["package_check", "backup", "restore_check", "lock", "preflight", "apply", "verify", "release"];
const idem = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
const errText = (e) => e?.response?.data?.detail || e.message;

export const MigrationControlPanel = () => {
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState(null);
  const [ack, setAck] = useState("");
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [auto, setAuto] = useState(false);
  const [log, setLog] = useState([]);
  const [receipt, setReceipt] = useState("");
  const autoRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const data = (await api.get("/admin/migration/status")).data;
      setInv(data); setErr(null);
      const open = (data.runs || []).find((r) => ["CREATED", "RUNNING", "FAILED", "ROLLBACK_IN_PROGRESS"].includes(r.status));
      if (open) setRun((prev) => prev || { id: open.id, status: open.status, phase: open.phase, attached: true });
    } catch (e) { setErr(errText(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const pushLog = (m) => setLog((l) => [{ at: new Date().toISOString().slice(11, 19), m }, ...l].slice(0, 60));

  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/migration/runs", { target_ack: ack });
      setRun(r.data); pushLog(`run ${r.data.id} created (phase ${r.data.phase})`); toast.success("Run created"); load();
    } catch (e) { toast.error(errText(e)); pushLog(`refused: ${errText(e)}`); } finally { setBusy(false); }
  };

  const stepOnce = async (id) => {
    const r = await api.post(`/admin/migration/runs/${id}/step`, {}, { headers: { "Idempotency-Key": idem() } });
    setRun((prev) => ({ ...(prev || {}), ...r.data, id }));
    pushLog(`${r.data.phase}${r.data.progress ? ` · ${r.data.progress}` : ""}${r.data.error ? ` · ${r.data.error}` : ""}`);
    return r.data;
  };

  const step = async () => {
    if (!run) return;
    setBusy(true);
    try { await stepOnce(run.id); } catch (e) { toast.error(errText(e)); pushLog(`refused: ${errText(e)}`); } finally { setBusy(false); load(); }
  };

  const runAuto = async (untilLock = false) => {
    if (!run) return;
    if (untilLock && !["package_check", "backup", "restore_check"].includes(run.phase)) { pushLog(`Checkpoint A already reached (phase ${run.phase}) — copy the receipt`); return; }
    autoRef.current = true; setAuto(true);
    try {
      while (autoRef.current) {
        const r = await stepOnce(run.id);
        if (r.done || r.error) break;
        if (untilLock && r.phase === "lock" && r.phase_finished) { pushLog("Checkpoint A reached: package verified, backup VERIFIED, restore-check PASS. Stopped before the lock — copy the receipt"); toast.success("Checkpoint A reached"); break; }
      }
    } catch (e) { toast.error(errText(e)); pushLog(`stopped: ${errText(e)}`); }
    autoRef.current = false; setAuto(false); load();
  };

  const copyReceipt = async () => {
    try {
      const status = (await api.get("/admin/migration/status")).data;
      const detail = run ? (await api.get(`/admin/migration/runs/${run.id}`)).data : null;
      const strip = (o) => { if (o && typeof o === "object") { for (const k of ["created_by", "owner_user_id", "admin_user_ids", "step_lease_user", "x_forwarded_for_tail"]) delete o[k]; Object.values(o).forEach(strip); } return o; };
      const text = JSON.stringify(strip({ captured_at: new Date().toISOString(), status, run: detail }), null, 1);
      setReceipt(text);
      try { await navigator.clipboard.writeText(text); toast.success("Receipt copied — paste it into the chat"); } catch { toast.message("Receipt shown below — select all and copy"); }
    } catch (e) { toast.error(errText(e)); }
  };

  const retryVerify = async () => {
    if (!run) return;
    setBusy(true);
    try {
      const r = (await api.post(`/admin/migration/runs/${run.id}/retry-verify`, { ack })).data;
      const rows = r.exception?.rows || [];
      pushLog(`verification retry authorised: ${rows.length} ${r.exception?.collection} row(s) differ from the backup only in ${[...new Set(rows.flatMap((x) => Object.keys(x.changed)))].join(", ")} — run back to RUNNING at verify; click Run all steps`);
      setRun({ id: r.run_id, status: r.status, phase: r.phase });
      toast.success("Verification retry authorised");
    } catch (e) { toast.error(errText(e)); pushLog(`refused: ${errText(e)}`); }
    setBusy(false); load();
  };

  const rollback = async () => {
    if (!run) return;
    setBusy(true);
    try {
      let r;
      do {
        r = (await api.post(`/admin/migration/runs/${run.id}/rollback`, { ack }, { headers: { "Idempotency-Key": idem() } })).data;
        pushLog(`rollback ${r.phase}${r.progress ? ` · ${r.progress}` : ""}`);
      } while (!r.done);
      setRun((p) => ({ ...p, ...r })); toast.success("Rolled back");
    } catch (e) { toast.error(errText(e)); pushLog(`rollback refused: ${errText(e)}`); } finally { setBusy(false); load(); }
  };

  const abort = async () => {
    if (!run) return;
    try { const r = await api.post(`/admin/migration/runs/${run.id}/abort`); setRun((p) => ({ ...p, ...r.data })); pushLog(`aborted (lock retained: ${r.data.lock_retained})`); } catch (e) { toast.error(errText(e)); } finally { load(); }
  };

  if (err) return <p data-testid="migration-control-error" className="text-sm text-red-700">{err}</p>;
  if (!inv) return <LoadingState testId="migration-control-loading" />;
  const legacyPresent = Object.keys(inv.legacy_documents_present || {}).length > 0;
  return (
    <div data-testid="migration-control" className="space-y-5">
      <section className="rounded-[10px] border border-jigsaw-border bg-white p-4">
        <h3 className="text-base font-semibold text-jigsaw-navy md:text-lg">Live inventory (read-only)</h3>
        <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-[13px] sm:grid-cols-4">
          <dt className="text-jigsaw-muted">Database</dt><dd data-testid="mig-db" className="font-mono">{inv.database}</dd>
          <dt className="text-jigsaw-muted">Cluster</dt><dd data-testid="mig-cluster" className="font-mono">{inv.cluster?.fingerprint} · {inv.cluster?.topology}</dd>
          <dt className="text-jigsaw-muted">Control</dt><dd data-testid="mig-enabled">{inv.enabled ? "ENABLED" : "DORMANT (MIGRATION_CONTROL_ENABLED not set)"}</dd>
          <dt className="text-jigsaw-muted">Lock</dt><dd data-testid="mig-lock">{inv.lock?.active ? `ACTIVE · ${inv.lock.token}` : `inactive${inv.lock?.outcome ? ` · last ${inv.lock.outcome}` : ""}`}</dd>
          <dt className="text-jigsaw-muted">Package pin</dt><dd className="font-mono">{inv.package_pin_expected?.slice(0, 16)}…</dd>
          <dt className="text-jigsaw-muted">Legacy present</dt><dd data-testid="mig-legacy-present">{legacyPresent ? Object.entries(inv.legacy_documents_present).map(([k, v]) => `${k}: ${v}`).join(", ") : "none"}</dd>
          <dt className="text-jigsaw-muted">Ingress peer seen</dt><dd data-testid="mig-peer" className="font-mono">{inv.request_chain?.socket_peer} · hops {inv.request_chain?.x_forwarded_for_entries}</dd>
        </dl>
        <details className="mt-2 text-xs"><summary className="cursor-pointer text-jigsaw-muted">Collection counts ({Object.keys(inv.counts || {}).length})</summary>
          <ul data-testid="mig-counts" className="mt-1 grid grid-cols-2 gap-x-4 sm:grid-cols-3 lg:grid-cols-4">{Object.entries(inv.counts || {}).map(([k, v]) => <li key={k} className="flex justify-between font-mono"><span>{k}</span><span>{v}</span></li>)}</ul></details>
        <Button data-testid="mig-refresh" size="sm" variant="outline" className="mt-2" onClick={load}>Refresh</Button>
      </section>

      <section className="rounded-[10px] border border-jigsaw-border bg-white p-4">
        <h3 className="text-base font-semibold text-jigsaw-navy md:text-lg">Promotion run</h3>
        <p className="mt-1 text-xs text-jigsaw-muted">Sequence: {PHASES.join(" → ")}. Backup and restore-check complete before the lock; the lock puts the platform into maintenance (non-admin sign-in and all authenticated writes refused). Insert-only; natives are never written.</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Input data-testid="mig-ack" className="h-8 w-64 font-mono" placeholder="type the database name to acknowledge" value={ack} onChange={(e) => setAck(e.target.value)} />
          <Button data-testid="mig-create" size="sm" disabled={!inv.enabled || busy || ack !== inv.database} onClick={create}>Create run</Button>
          <Button data-testid="mig-step" size="sm" variant="outline" disabled={!run || busy || auto} onClick={step}>Advance one step</Button>
          <Button data-testid="mig-to-lock" size="sm" className="bg-jigsaw-navy text-white hover:bg-jigsaw-navy/90" disabled={!run || busy || auto} onClick={() => runAuto(true)}>Run to lock (Checkpoint A)</Button>
          <Button data-testid="mig-auto" size="sm" className="bg-jigsaw-orange text-white hover:bg-jigsaw-orangeDark" disabled={!run || busy || auto} onClick={() => runAuto(false)}>Run all steps</Button>
          {auto && <Button data-testid="mig-stop" size="sm" variant="outline" onClick={() => { autoRef.current = false; }}>Stop after this step</Button>}
          <Button data-testid="mig-abort" size="sm" variant="outline" disabled={!run || busy} onClick={abort}>Abort</Button>
          <Button data-testid="mig-rollback" size="sm" variant="destructive" disabled={!run || busy || ack !== inv.database} onClick={rollback}>Roll back this run</Button>
          {run?.status === "FAILED" && run?.phase === "verify" && <Button data-testid="mig-retry-verify" size="sm" className="bg-jigsaw-navy text-white hover:bg-jigsaw-navy/90" disabled={busy || ack !== inv.database} onClick={retryVerify}>Retry verification</Button>}
          <Button data-testid="mig-copy-receipt" size="sm" variant="outline" disabled={busy || auto} onClick={copyReceipt}>Copy receipt</Button>
        </div>
        {receipt && <textarea data-testid="mig-receipt" readOnly className="mt-3 h-40 w-full rounded border border-jigsaw-border p-2 font-mono text-[11px]" value={receipt} onFocus={(e) => e.target.select()} />}
        {run && (
          <div data-testid="mig-run" className="mt-3 rounded-lg border border-jigsaw-border bg-jigsaw-bg p-3 text-[13px]">
            <div className="flex flex-wrap gap-x-4"><span>Run <span className="font-mono">{run.id}</span></span><span>Status <b data-testid="mig-run-status">{run.status}</b></span><span>Phase <b data-testid="mig-run-phase">{run.phase}</b></span>{run.attached && <span data-testid="mig-run-attached" className="text-jigsaw-muted">re-attached to the open run (resume with Advance one step)</span>}</div>
            {run.progress && <p data-testid="mig-run-progress" className="mt-1 text-jigsaw-muted">{run.progress}</p>}
            {run.error && <p data-testid="mig-run-error" className="mt-1 text-red-700">{run.error}</p>}
          </div>
        )}
        {log.length > 0 && <ul data-testid="mig-log" className="mt-3 max-h-56 overflow-auto rounded border border-jigsaw-border bg-white p-2 font-mono text-[11px]">{log.map((l, i) => <li key={i}>{l.at} {l.m}</li>)}</ul>}
      </section>
    </div>
  );
};
